import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VerifiedIcon from '@mui/icons-material/Verified';
import { ROUTES } from '../../app/routes';
import { useAppSelector } from '../../app/store';
import { feedApi } from '../../core/api/client';
import { platformHubUrl } from '../../core/config';
import { http } from '../../core/http/client';
import type { ProfilePhoto } from '../hub/hubApi';
import { publicApi, type PostCard, type PostComment, type PostReportReason } from './publicApi';
import { companyPath, usePublicLanguage } from './components';

/** Signed-in actions on public posts (`/api/v1/platform/social/posts/*`); any session may use them. */
export const socialApi = {
  async liked(ids: string[]): Promise<string[]> {
    if (ids.length === 0) return [];
    const { data } = await http().get<string[]>('/social/posts/liked', { params: { ids: ids.join(',') }, skipErrorToast: true });
    return data;
  },
  /** Which of `ids` the caller wrote in their current organization (and so may delete). */
  async own(ids: string[]): Promise<string[]> {
    if (ids.length === 0) return [];
    const { data } = await http().get<string[]>('/social/posts/own', { params: { ids: ids.join(',') }, skipErrorToast: true });
    return data;
  },
  async like(postId: string, liked: boolean): Promise<{ liked: boolean; likeCount: number }> {
    const path = `/social/posts/${encodeURIComponent(postId)}/likes`;
    const { data } = liked ? await http().post(path) : await http().delete(path);
    return data;
  },
  async comment(postId: string, content: string): Promise<PostComment> {
    const { data } = await http().post<PostComment>(`/social/posts/${encodeURIComponent(postId)}/comments`, { content });
    return data;
  },
  async report(postId: string, reason: PostReportReason, details?: string): Promise<void> {
    await http().post(`/social/posts/${encodeURIComponent(postId)}/reports`, { reason, details: details || undefined });
  },
};

export function postPath(postId: string) {
  return `${ROUTES.social}?post=${encodeURIComponent(postId)}`;
}

/** Which of the shown posts the signed-in visitor has liked; empty for a visitor without a session. */
export function useLikedPosts(posts: PostCard[] | null) {
  const signedIn = useAppSelector((state) => state.session.status) === 'authenticated';
  const [liked, setLiked] = useState<Set<string>>(() => new Set());
  const ids = (posts ?? []).map((post) => post.id).join(',');
  useEffect(() => {
    if (!signedIn || !ids) { setLiked(new Set()); return; }
    let active = true;
    socialApi.liked(ids.split(',')).then((rows) => { if (active) setLiked(new Set(rows)); }).catch(() => undefined);
    return () => { active = false; };
  }, [signedIn, ids]);
  return liked;
}

/** Which of the shown posts the signed-in visitor wrote (offered a Delete); empty without a session. */
export function useOwnPosts(posts: PostCard[] | null) {
  const signedIn = useAppSelector((state) => state.session.status) === 'authenticated';
  const [own, setOwn] = useState<Set<string>>(() => new Set());
  const ids = (posts ?? []).map((post) => post.id).join(',');
  useEffect(() => {
    if (!signedIn || !ids) { setOwn(new Set()); return; }
    let active = true;
    socialApi.own(ids.split(',')).then((rows) => { if (active) setOwn(new Set(rows)); }).catch(() => undefined);
    return () => { active = false; };
  }, [signedIn, ids]);
  return own;
}

function relativeTime(iso: string, lang: string) {
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  if (!Number.isFinite(seconds)) return '';
  const locale = lang === 'al' ? 'sq' : lang;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [['year', 31_536_000], ['month', 2_592_000], ['week', 604_800], ['day', 86_400], ['hour', 3_600], ['minute', 60]];
  try {
    const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
    for (const [unit, size] of units) if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
    return format.format(0, 'minute');
  } catch {
    return new Date(iso).toLocaleDateString();
  }
}

/**
 * Swipeable photos: one photo per snap point, dots underneath when there is more than one, arrows
 * on hover for a mouse. The dots follow the scroll position, so a swipe and a click agree.
 */
export function PostPhotos({ media, alt }: { media: ProfilePhoto[]; alt: string }) {
  const { t } = useTranslation();
  const track = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const onScroll = useCallback(() => {
    const el = track.current;
    if (el && el.clientWidth > 0) setIndex(Math.round(el.scrollLeft / el.clientWidth));
  }, []);
  const go = (next: number) => {
    const el = track.current;
    const target = Math.max(0, Math.min(media.length - 1, next));
    setIndex(target);
    el?.scrollTo({ left: target * el.clientWidth, behavior: 'smooth' });
  };
  if (media.length === 0) return null;
  const many = media.length > 1;
  return <div className="hub-post-photos">
    <div ref={track} className="hub-post-track" onScroll={onScroll} tabIndex={many ? 0 : undefined}
      aria-roledescription={many ? 'carousel' : undefined} aria-label={many ? t('hub.posts.photos', 'Photos') : undefined}
      onKeyDown={(event) => { if (event.key === 'ArrowRight') { event.preventDefault(); go(index + 1); } if (event.key === 'ArrowLeft') { event.preventDefault(); go(index - 1); } }}>
      {media.map((photo, position) => <div key={photo.mediaId ?? position} className="hub-post-slide"
        aria-label={many ? t('hub.posts.photoOf', 'Photo {{index}} of {{count}}', { index: position + 1, count: media.length }) : undefined}>
        <img src={photo.cardUrl} srcSet={photo.srcset ?? undefined} sizes="(max-width: 600px) 100vw, 420px" alt={position === 0 ? alt : ''}
          width={photo.width ?? undefined} height={photo.height ?? undefined} loading="lazy" decoding="async" draggable={false}
          style={{ backgroundColor: photo.dominantColor ?? undefined, backgroundImage: photo.lqipUrl ? `url(${photo.lqipUrl})` : undefined }} />
      </div>)}
    </div>
    {many && <>
      <span className="hub-post-count" aria-hidden>{index + 1}/{media.length}</span>
      <button type="button" className="hub-post-arrow prev" onClick={() => go(index - 1)} disabled={index === 0} aria-label={t('hub.posts.previousPhoto', 'Previous photo')}><ChevronLeftIcon sx={{ fontSize: 20 }} aria-hidden /></button>
      <button type="button" className="hub-post-arrow next" onClick={() => go(index + 1)} disabled={index === media.length - 1} aria-label={t('hub.posts.nextPhoto', 'Next photo')}><ChevronRightIcon sx={{ fontSize: 20 }} aria-hidden /></button>
      <div className="hub-post-dots" role="group" aria-label={t('hub.posts.choosePhoto', 'Choose photo')}>
        {media.map((photo, position) => <button type="button" key={photo.mediaId ?? position} aria-current={position === index}
          aria-label={t('hub.posts.photoOf', 'Photo {{index}} of {{count}}', { index: position + 1, count: media.length })} onClick={() => go(position)} />)}
      </div>
    </>}
  </div>;
}

const REASONS: PostReportReason[] = ['Spam', 'Inappropriate', 'Misleading', 'Harassment', 'Other'];

/** "⋯" menu in the post header; closes on an outside click or Escape. */
function PostMenu({ label, children }: { label: string; children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return <div className="hub-post-menu" ref={root}>
    <button type="button" className="hub-post-menu-toggle" aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
      <MoreHorizRoundedIcon aria-hidden />
    </button>
    {open && <div className="hub-post-menu-list" role="menu">{children(() => setOpen(false))}</div>}
  </div>;
}

/**
 * Social post: business header with a "⋯" menu (report; delete for the author), swipeable photos,
 * caption, a likes · comments line, a Like · Comment · Share bar, then the comment thread.
 * `own` comes from `useOwnPosts`; `onDeleted` lets the list drop the card (it hides itself otherwise).
 */
export function PostCardView({ post, liked: initiallyLiked = false, own = false, onDeleted }: {
  post: PostCard; liked?: boolean; own?: boolean; onDeleted?: (postId: string) => void;
}) {
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const navigate = useNavigate();
  const signedIn = useAppSelector((state) => state.session.status) === 'authenticated';
  const [liked, setLiked] = useState(initiallyLiked);
  const [likeCount, setLikeCount] = useState(post.likeCount);
  const [commentCount, setCommentCount] = useState(post.commentCount);
  const [expanded, setExpanded] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState<PostComment[] | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState<PostReportReason>('Spam');
  const [reported, setReported] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [failedLogo, setFailedLogo] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const commentInput = useRef<HTMLInputElement>(null);

  useEffect(() => setLiked(initiallyLiked), [initiallyLiked]);
  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 2500);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const requireSession = () => {
    if (signedIn) return true;
    navigate(ROUTES.login);
    return false;
  };

  async function toggleLike() {
    if (!requireSession() || busy) return;
    const next = !liked;
    setLiked(next);
    setLikeCount((count) => Math.max(0, count + (next ? 1 : -1)));
    setBusy(true);
    try {
      const result = await socialApi.like(post.id, next);
      setLiked(result.liked);
      setLikeCount(result.likeCount);
    } catch {
      setLiked(!next);
      setLikeCount((count) => Math.max(0, count + (next ? -1 : 1)));
      setNotice(t('hub.posts.actionFailed', 'That did not work. Please try again.'));
    } finally { setBusy(false); }
  }

  async function openComments() {
    const opening = !commentsOpen;
    setCommentsOpen(opening);
    if (opening && comments === null) {
      try { setComments(await publicApi.postComments(post.id)); }
      catch { setComments([]); setNotice(t('hub.posts.commentsFailed', 'Comments could not be loaded.')); }
    }
    if (opening) window.setTimeout(() => commentInput.current?.focus(), 0);
  }

  async function submitComment(event: FormEvent) {
    event.preventDefault();
    const content = draft.trim();
    if (!content || !requireSession()) return;
    setBusy(true);
    try {
      const comment = await socialApi.comment(post.id, content);
      setComments((current) => [...(current ?? []), comment]);
      setCommentCount((count) => count + 1);
      setDraft('');
    } catch { setNotice(t('hub.posts.actionFailed', 'That did not work. Please try again.')); }
    finally { setBusy(false); }
  }

  async function share() {
    const url = new URL(postPath(post.id), window.location.origin).toString();
    const title = post.business.name;
    try {
      if (navigator.share) { await navigator.share({ title, text: post.content.slice(0, 120), url }); return; }
      await navigator.clipboard.writeText(url);
      setNotice(t('hub.posts.linkCopied', 'Link copied'));
    } catch (error) {
      if ((error as DOMException)?.name !== 'AbortError') setNotice(url);
    }
  }

  async function submitReport(event: FormEvent) {
    event.preventDefault();
    if (!requireSession()) return;
    setBusy(true);
    try {
      await socialApi.report(post.id, reason);
      setReported(true);
      setReportOpen(false);
      setNotice(t('hub.posts.reported', 'Thanks. We will review this post.'));
    } catch { setNotice(t('hub.posts.actionFailed', 'That did not work. Please try again.')); }
    finally { setBusy(false); }
  }

  async function deletePost() {
    setBusy(true);
    try {
      await feedApi.deletePost(post.id);
      setConfirmDelete(false);
      if (onDeleted) onDeleted(post.id); else setDeleted(true);
    } catch { setNotice(t('hub.posts.deleteFailed', 'The post could not be deleted.')); }
    finally { setBusy(false); }
  }

  const reasonLabel = (value: PostReportReason) => ({
    Spam: t('hub.posts.reason.Spam', 'Spam'),
    Inappropriate: t('hub.posts.reason.Inappropriate', 'Inappropriate content'),
    Misleading: t('hub.posts.reason.Misleading', 'False or misleading'),
    Harassment: t('hub.posts.reason.Harassment', 'Harassment or hate'),
    Other: t('hub.posts.reason.Other', 'Something else'),
  })[value];

  const long = post.content.length > 160;
  const { business } = post;
  if (deleted) return null;

  return <article className="hub-post" id={`post-${post.id}`} aria-label={t('hub.posts.postBy', 'Post by {{name}}', { name: business.name })}>
    <header className="hub-post-head">
      <Link to={companyPath(business.slug)} className="hub-post-avatar" aria-hidden tabIndex={-1}>
        {business.logoUrl && failedLogo !== business.logoUrl ? <img src={platformHubUrl(business.logoUrl)} alt="" onError={() => setFailedLogo(business.logoUrl)} /> : <span>{business.name.slice(0, 1).toUpperCase()}</span>}
      </Link>
      <div className="hub-post-who">
        <Link to={companyPath(business.slug)} className="hub-post-name">
          {business.name}
          {business.isVerified && <VerifiedIcon titleAccess={t('hub.card.verified', 'Verified')} sx={{ fontSize: 15, color: 'var(--hub-accent)', ml: 0.5, verticalAlign: '-2px' }} />}
        </Link>
        <span className="hub-muted">{[business.city, relativeTime(post.createdAt, lang)].filter(Boolean).join(' · ')}</span>
      </div>
      {business.groupCode && <span className={`hub-pill ${business.groupCode}`}>{business.groupName ?? business.groupCode}</span>}
      <PostMenu label={t('hub.posts.options', 'Post options')}>
        {(close) => <>
          {own && <button type="button" role="menuitem" className="danger" onClick={() => { close(); setReportOpen(false); setConfirmDelete(true); }}>
            <DeleteOutlineRoundedIcon aria-hidden />{t('hub.posts.delete', 'Delete post')}
          </button>}
          {!own && <button type="button" role="menuitem" disabled={reported}
            onClick={() => { close(); if (requireSession()) { setConfirmDelete(false); setReportOpen(true); } }}>
            <OutlinedFlagIcon aria-hidden />{reported ? t('hub.posts.reportedShort', 'Reported') : t('hub.posts.report', 'Report')}
          </button>}
        </>}
      </PostMenu>
    </header>

    {confirmDelete && <div className="hub-post-confirm" role="alertdialog" aria-label={t('hub.posts.delete', 'Delete post')}>
      <span>{t('hub.posts.deleteConfirm', 'Delete this post? This cannot be undone.')}</span>
      <div>
        <button type="button" className="hub-btn sm ghost" onClick={() => setConfirmDelete(false)} disabled={busy}>{t('hub.posts.cancel', 'Cancel')}</button>
        <button type="button" className="hub-btn sm danger" onClick={deletePost} disabled={busy}>{t('hub.posts.delete', 'Delete post')}</button>
      </div>
    </div>}

    {post.content && <p className={`hub-post-caption${long && !expanded ? ' clamp' : ''}`}>{post.content}</p>}
    {long && <button type="button" className="hub-post-more" onClick={() => setExpanded((value) => !value)}>
      {expanded ? t('hub.posts.less', 'less') : t('hub.posts.more', 'more')}
    </button>}

    <PostPhotos media={post.media} alt={post.content.slice(0, 100) || business.name} />

    {(likeCount > 0 || commentCount > 0) && <div className="hub-post-stats">
      {likeCount > 0 && <span className="likes"><span className="hub-post-heart" aria-hidden><FavoriteRoundedIcon /></span>{t('hub.posts.likesCount', '{{count}} likes', { count: likeCount })}</span>}
      {commentCount > 0 && <button type="button" onClick={openComments}>{t('hub.posts.commentsCount', '{{count}} comments', { count: commentCount })}</button>}
    </div>}

    <div className="hub-post-actions">
      <button type="button" className={liked ? 'liked' : undefined} aria-pressed={liked} onClick={toggleLike}
        aria-label={liked ? t('hub.posts.unlike', 'Unlike') : t('hub.posts.like', 'Like')}>
        {liked ? <FavoriteRoundedIcon aria-hidden /> : <FavoriteBorderRoundedIcon aria-hidden />}<span>{t('hub.posts.like', 'Like')}</span>
      </button>
      <button type="button" className={commentsOpen ? 'active' : undefined} aria-expanded={commentsOpen} onClick={openComments}>
        <ChatBubbleOutlineRoundedIcon aria-hidden /><span>{t('hub.posts.comment', 'Comment')}</span>
      </button>
      <button type="button" onClick={share}><ShareRoundedIcon aria-hidden /><span>{t('hub.posts.share', 'Share')}</span></button>
    </div>

    {notice && <p className="hub-post-notice" role="status">{notice}</p>}

    {reportOpen && <form className="hub-post-report" onSubmit={submitReport}>
      <fieldset>
        <legend>{t('hub.posts.reportTitle', 'Why are you reporting this post?')}</legend>
        {REASONS.map((value) => <label key={value}>
          <input type="radio" name={`reason-${post.id}`} value={value} checked={reason === value} onChange={() => setReason(value)} /> {reasonLabel(value)}
        </label>)}
      </fieldset>
      <div className="hub-post-report-actions">
        <button type="button" className="hub-btn sm ghost" onClick={() => setReportOpen(false)}>{t('hub.posts.cancel', 'Cancel')}</button>
        <button type="submit" className="hub-btn sm" disabled={busy}>{t('hub.posts.sendReport', 'Report')}</button>
      </div>
    </form>}

    {commentsOpen && <div className="hub-post-comments">
      {comments === null && <span className="hub-muted">{t('hub.loading', 'Loading')}…</span>}
      {comments?.length === 0 && <span className="hub-muted">{t('hub.posts.noComments', 'No comments yet.')}</span>}
      {comments && comments.length > 0 && <ul>
        {comments.map((comment) => <li key={comment.id}>
          <span className="hub-post-comment-avatar" aria-hidden>{comment.authorName.slice(0, 1).toUpperCase()}</span>
          <div className="hub-post-comment-bubble"><b>{comment.authorName}</b><span>{comment.content}</span></div>
        </li>)}
      </ul>}
      {signedIn
        ? <form className="hub-post-comment-form" onSubmit={submitComment}>
          <label className="hub-sr" htmlFor={`comment-${post.id}`}>{t('hub.posts.addComment', 'Add a comment…')}</label>
          <input ref={commentInput} id={`comment-${post.id}`} value={draft} maxLength={2000} placeholder={t('hub.posts.addComment', 'Add a comment…')}
            onChange={(event) => setDraft(event.target.value)} />
          <button type="submit" disabled={busy || !draft.trim()} aria-label={t('hub.posts.post', 'Post')}><SendRoundedIcon aria-hidden /></button>
        </form>
        : <Link className="hub-post-signin" to={ROUTES.login}>{t('hub.posts.signInToComment', 'Sign in to comment')}</Link>}
    </div>}
  </article>;
}
